import { useMemo, useState } from "react";

import type {
  SanityCompareTableQueryResult,
  SanityCompareView,
} from "./compare.type";

export function useCompareTableSectionState(
  block: SanityCompareTableQueryResult,
) {
  const { views, filterOptions } = useMemo(() => {
    const views = block.views.filter(isUsableCompareView);
    return {
      views,
      filterOptions: views.map((v) => ({
        value: v.viewKey,
        label: v.tabLabel,
      })),
    };
  }, [block.views]);

  const [filter, setFilter] = useState(() => views[0]?.viewKey ?? "");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [mobileCompareRightId, setMobileCompareRight] = useState("");

  const activeView = useMemo(
    () => views.find((v) => v.viewKey === filter) ?? views[0],
    [views, filter],
  );

  const table = useMemo(() => {
    if (!activeView) return null;
    const columns = activeView.columns ?? [];
    const rows = activeView.rows ?? [];
    if (!columns.length) return null;

    const leftPreferred =
      selectedProduct && columns.some((c) => c.id === selectedProduct)
        ? selectedProduct
        : columns[0].id;

    const { left: compareLeftId, right: compareRightId } = resolveComparePair(
      columns,
      leftPreferred,
      mobileCompareRightId,
    );

    const compareLeftIndex = columns.findIndex((c) => c.id === compareLeftId);
    const compareRightIndex = columns.findIndex((c) => c.id === compareRightId);

    const colIndex = compareLeftIndex;
    const selectedColumn =
      compareLeftIndex >= 0 ? columns[compareLeftIndex] : undefined;

    return {
      columns,
      rows,
      gridColumns: `198px repeat(${columns.length}, minmax(250px, 1fr))`,
      colIndex,
      selectedColumn,
      compareLeftId,
      compareRightId,
      compareLeftIndex,
      compareRightIndex,
    };
  }, [activeView, selectedProduct, mobileCompareRightId]);

  return {
    filterOptions,
    filter,
    setFilter,
    setSelectedProduct,
    setMobileCompareRight,
    activeView,
    table,
  };
}

function isUsableCompareView(view: SanityCompareView): boolean {
  const viewKey = view.viewKey?.trim();
  const tabLabel = view.tabLabel?.trim();
  const columns = view.columns ?? [];
  const rows = view.rows ?? [];
  if (!viewKey || !tabLabel || !columns.length || !rows.length) return false;
  if (!columns.every((c) => c.id && c.title && c.imageUrl)) return false;
  if (!rows.every((r) => r.id && r.label)) return false;
  return true;
}

function resolveComparePair(
  columns: CompareColumn[],
  preferredLeft: string,
  preferredRight: string,
): { left: string; right: string } {
  const ids = new Set(columns.map((c) => c.id));
  let left =
    preferredLeft && ids.has(preferredLeft) ? preferredLeft : columns[0].id;
  let right =
    preferredRight && ids.has(preferredRight)
      ? preferredRight
      : (columns[1]?.id ?? columns[0].id);

  if (left === right && columns.length > 1) {
    right = columns.find((c) => c.id !== left)?.id ?? right;
  }

  return { left, right };
}

type CompareColumn = SanityCompareView["columns"][number];
